import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router'
import { ArrowLeft } from 'lucide-react'
import { useAdminTrpc } from 'shared/api'
import { Button } from 'shared/components/ui/button'
import { CopyChip } from 'shared/components/CopyChip'
import { telegramChatUrl } from 'shared/lib/telegram-link'
import { formatUzsAmount } from 'shared/lib/format'
import { useTranslate } from 'shared/lib/i18n'
import { useAuthStore } from 'shared/store/auth'
import { PaymentsHistory } from 'pages/admin-purchases/ui/PaymentsHistory'
import { canViewClientFinance } from '../lib/permissions'
import { WalletSection } from './WalletSection'

export function ClientDetail() {
	const { id } = useParams<{ id: string }>()
	const trpc = useAdminTrpc()
	const navigate = useNavigate()
	const user = useAuthStore((state) => state.user)
	const showFinance = canViewClientFinance(user)
	const { t, prefixT } = useTranslate('pages.adminClients')
	const { data: client } = useQuery(trpc.users.GetById.queryOptions({ id: id! }))
	if (!client) return <div className='p-4'>{t('common.loading')}</div>
	const chat = telegramChatUrl(client.username, client.telegramId)
	return (
		<div className='flex flex-col gap-4 p-4'>
			<div className='flex items-center gap-2'>
				<Button variant='ghost' size='icon' onClick={() => navigate(-1)}>
					<ArrowLeft className='size-5' />
				</Button>
				<div className='flex-1'>
					<h1 className='text-lg font-semibold'>{client.name}</h1>
					{chat && (
						<a href={`${chat}${client.username ? '?profile' : ''}`} target='_blank' rel='noreferrer' className='text-sm underline'>
							{client.username ? `@${client.username}` : `id${client.telegramId}`}
						</a>
					)}
				</div>
				{client.tezId && <CopyChip value={client.tezId} />}
			</div>
			<div className='text-sm text-muted-foreground'>
				<div>
					{prefixT('purchasesAndItems', {
						purchases: String(client.purchaseCount),
						items: String(client.itemCount),
					})}
				</div>
				<div>
					{prefixT('grossAndNet', {
						gross: t('common.amountUzs', { amount: formatUzsAmount(client.grossPaidUzs) }),
						net: t('common.amountUzs', { amount: formatUzsAmount(client.netPaidUzs) }),
					})}
				</div>
			</div>
			{showFinance && (
				<>
					<WalletSection userId={client._id} />
					<h2 className='font-medium'>{prefixT('orders')}</h2>
					<PaymentsHistory buyerUserId={client._id} />
				</>
			)}
		</div>
	)
}
